import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { PHOTOS } from "@/data/content";
import { scrollToId } from "@/components/Navbar";

const ROLES = [
  "International Bestselling Author",
  "Inspirational Speaker",
  "Trauma & Grief Expert",
  "Thought Leader",
  "Talk Show Host",
  "Podcaster",
  "Producer",
  "Healer",
  "Intuitive Medium",
  "Grief & Addiction Counselor",
  "Near-Death Experiencer",
];

const STATS = [
  { value: "580M+", label: "Media views worldwide" },
  { value: "60K", label: "Clients guided & counting" },
  { value: "160K+", label: "Combined followers" },
  { value: "1,000s", label: "Shows, podcasts & stages" },
];

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.15, delay, ease: [0.76, 0, 0.24, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const [roleIdx, setRoleIdx] = useState(0);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "10%"]);

  useEffect(() => {
    const t = setInterval(() => setRoleIdx((i) => (i + 1) % ROLES.length), 2400);
    return () => clearInterval(t);
  }, []);

  return (
    <section ref={ref} data-testid="hero" className="relative overflow-hidden">
      <div className="relative w-full md:h-[100svh] md:min-h-[680px]">
        <motion.div className="relative h-[54svh] overflow-hidden md:absolute md:inset-0 md:h-full">
          <motion.img
            src={PHOTOS.hero}
            alt="Susan Grau"
            data-testid="hero-portrait"
            fetchPriority="high"
            initial={{ scale: 1.08, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 1.8, ease: [0.76, 0, 0.24, 1] }}
            className="h-full w-full object-cover object-[50%_8%] md:object-[62%_4%]"
          />
        </motion.div>
        <div
          className="absolute inset-0 hidden md:block"
          style={{ background: "linear-gradient(90deg, rgba(253,250,244,0.96) 0%, rgba(253,250,244,0.7) 38%, rgba(253,250,244,0.15) 62%, transparent 80%)" }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 hidden bg-gradient-to-t from-[#FDFAF4]/90 via-transparent to-transparent md:block" aria-hidden="true" />

        <div className="absolute left-0 right-0 top-24 z-10 hidden items-start justify-between px-6 md:flex lg:px-16">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2, duration: 0.8 }}>
            <p className="font-body text-[0.6rem] font-medium uppercase tracking-[0.35em] text-[#6B5D4F]">
              Healer · Author · Speaker
            </p>
            <p className="mt-1 font-body text-[0.55rem] uppercase tracking-[0.3em] text-[#8B7D6B]">
              The Infinite Life Portfolio · Issue 01
            </p>
          </motion.div>
          <motion.p
            className="hidden font-body text-[0.55rem] uppercase tracking-[0.3em] text-[#8B7D6B] md:block"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.35 }}
          >
            Est. three decades · 580M+ views worldwide
          </motion.p>
        </div>

        <motion.p
          className="absolute right-6 top-1/2 z-10 hidden -translate-y-1/2 font-body text-[0.55rem] font-medium uppercase tracking-[0.4em] text-[#8B7D6B] lg:block"
          style={{ writingMode: "vertical-rl" }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
        >
          Susan Grau · Cover Story · 2026
        </motion.p>

        <div className="relative z-10 bg-[#FDFAF4] px-6 pb-12 pt-10 md:flex md:h-full md:flex-col md:justify-end md:bg-transparent md:pt-0 lg:px-16">
          <div className="mb-6 hidden h-6 items-center overflow-hidden md:flex">
            <AnimatePresence mode="wait">
              <motion.span
                key={roleIdx}
                data-testid="hero-rotating-role"
                className="font-body text-[0.65rem] font-medium uppercase tracking-[0.4em] text-[#C5A059]"
                initial={{ y: 18, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -18, opacity: 0 }}
                transition={{ duration: 0.5, ease: [0.76, 0, 0.24, 1] }}
              >
                {ROLES[roleIdx]}
              </motion.span>
            </AnimatePresence>
          </div>

          <h1 className="font-display2 uppercase leading-[0.85] tracking-[0.01em] text-[#6B5D4F]">
            <MaskedLine delay={0.4} className="text-[16vw] md:text-[11.5vw]">
              Susan
            </MaskedLine>
            <MaskedLine delay={0.55} className="text-[16vw] md:text-[11.5vw]">
              Grau
            </MaskedLine>
            <MaskedLine delay={0.75} className="mt-4">
              <span className="font-serif2 normal-case italic tracking-normal text-[#C5A059] text-[7vw] md:text-[4.5vw]">
                speaks in <span className="border-b-2 border-[#C5A059]">hope.</span>
              </span>
            </MaskedLine>
          </h1>

          <motion.div
            className="mt-9 flex max-w-2xl flex-col gap-7"
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05, ease: [0.76, 0, 0.24, 1] }}
          >
            <p className="font-serif2 text-xl italic leading-snug text-[#6B5D4F]/80 md:text-2xl">
              Transforming profound loss into resilience, hope, and purpose —{" "}
              <span className="text-[#C5A059]">the voice the press keeps calling.</span>
            </p>
            <div>
              <div className="flex flex-wrap items-center gap-4">
                <button
                  data-testid="hero-book-btn"
                  onClick={() => scrollToId("#press")}
                  className="group relative overflow-hidden bg-[#6B5D4F] px-9 py-4 font-body text-[0.7rem] font-medium uppercase tracking-[0.3em] text-[#FDFAF4]"
                >
                  <span className="absolute inset-0 -translate-x-full bg-[#C5A059] transition-transform duration-500 ease-[cubic-bezier(0.76,0,0.24,1)] group-hover:translate-x-0" />
                  <span className="relative transition-colors duration-500 group-hover:text-[#6B5D4F]">Book Susan</span>
                </button>
                <button
                  data-testid="hero-story-btn"
                  onClick={() => scrollToId("#story")}
                  className="border border-[#6B5D4F]/30 px-9 py-4 font-body text-[0.7rem] font-medium uppercase tracking-[0.3em] text-[#6B5D4F] transition-colors duration-500 hover:border-[#C5A059] hover:text-[#C5A059]"
                >
                  Explore Her Story
                </button>
              </div>
              <p className="mt-4 font-body text-[0.55rem] font-medium uppercase tracking-[0.3em] text-[#8B7D6B]">
                Media & bookings — Meghan Amaral, M Media Corp
              </p>
            </div>
          </motion.div>
        </div>
      </div>

      <motion.div
        data-testid="hero-stats"
        className="relative z-10 grid grid-cols-2 gap-px border-t border-[#6B5D4F]/10 bg-[#FDFAF4] px-6 md:grid-cols-4 lg:px-16"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
      >
        {STATS.map((s, i) => (
          <div key={s.label} data-testid={`hero-stat-${i}`} className="flex flex-col gap-1 border-l border-[#6B5D4F]/10 py-8 pl-6 first:border-l-0 first:pl-0">
            <span className="font-display text-4xl font-medium text-[#6B5D4F]">{s.value}</span>
            <span className="font-body text-[0.65rem] font-medium uppercase tracking-[0.25em] text-[#8B7D6B]">{s.label}</span>
          </div>
        ))}
      </motion.div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollToId("#story")}
        className="absolute bottom-32 left-1/2 hidden -translate-x-1/2 text-[#C5A059] lg:block"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        aria-label="Scroll down"
      >
        <ArrowDown size={20} strokeWidth={1.5} />
      </motion.button>
    </section>
  );
}

